// Create/edit form for managed contracts — type selector, supplier combobox,
// lookup dropdowns, extension options, person references, links.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Button, Alert, LoadingSpinner } from "../../components/common";
import SupplierCombobox from "../../components/manage/SupplierCombobox";
import SupplierFormModal from "../../components/manage/SupplierFormModal";
import CurrencyInput from "../../components/manage/CurrencyInput";
import PersonReferenceInput from "../../components/manage/PersonReferenceInput";
import ExternalLinksList from "../../components/manage/ExternalLinksList";
import ExtensionOptionsInput from "../../components/manage/ExtensionOptionsInput";
import ContractSettingsCard, {
  type GraceByFrequency,
  type GraceMode,
} from "../../components/manage/ContractSettingsCard";
import { formatBackendError } from "../../utils/formatBackendError";
import { useLookups } from "../../hooks/useLookups";
import {
  useCreateManagedContract,
  useManagedContract,
  useUpdateManagedContract,
} from "../../hooks/useManagedContracts";
import { useCreateSupplier } from "../../hooks/useSuppliers";
import { useOrgUsers } from "../../hooks/useOrgUsers";
import { contractToFormState } from "./managedContractFormState";
import type {
  ManagedContractType,
  PersonReference,
  ExternalLink,
  ExtensionOptions,
  ContractPerson,
  CreateManagedContractInput,
  UpdateManagedContractInput,
} from "../../types";

function buildContractPeople(
  owner: PersonReference | undefined,
  manager: PersonReference | undefined,
): ContractPerson[] {
  const out: ContractPerson[] = [];
  if (owner?.name) {
    out.push({
      role: "owner",
      name: owner.name,
      email: owner.email,
      alertsEnabled: owner.alertsEnabled ?? true,
      // When the owner is linked to a system user, the backend uses
      // userId to fan out ACL auto-grants; preserve it through the
      // submit payload.
      ...(owner.userId && { userId: owner.userId }),
    });
  }
  if (manager?.name) {
    out.push({
      role: "manager",
      name: manager.name,
      email: manager.email,
      // Manager's alertsEnabled is server-enforced to true; the form
      // mirrors this via alertsLocked on PersonReferenceInput. Hardcoding
      // here keeps payloads correct even if a stale state slips through.
      alertsEnabled: true,
      ...(manager.userId && { userId: manager.userId }),
    });
  }
  return out;
}

export default function ManagedContractForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const createMutation = useCreateManagedContract();
  const updateMutation = useUpdateManagedContract();
  const createSupplierMutation = useCreateSupplier();
  const { data: existingContract, isLoading: isLoadingContract } =
    useManagedContract(id || "");

  const [showSupplierModal, setShowSupplierModal] = useState(false);

  const { data: contractTypes } = useLookups("contract-type");
  const { data: businessUnits } = useLookups("business-unit");
  const { data: procurementMethods } = useLookups("procurement-method");
  // Surface org users to the owner/manager PersonReferenceInputs so the
  // admin can link a system user instead of typing a free-text contact.
  // When the user is linked, the backend auto-grants an ACL row when
  // their OrgUser is contractAccess='selected'.
  const { data: orgUsersData } = useOrgUsers();
  const availableUsers = orgUsersData?.items ?? [];

  const [type, setType] = useState<ManagedContractType>("contract");
  const [contractNumber, setContractNumber] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [contractTypeId, setContractTypeId] = useState("");
  const [businessUnitId, setBusinessUnitId] = useState("");
  const [procurementMethodId, setProcurementMethodId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [value, setValue] = useState(0);
  // Two PersonReference inputs are bound to the canonical owner/manager
  // entries of the contractPeople[] array. The form does not surface
  // non-canonical roles (project-manager, free-text) — those are added
  // on the dedicated Access & People tab post-create. alertsEnabled
  // defaults to true on both (matching the mapper synthesis); the
  // manager's value is server-enforced to true regardless.
  const [contractManager, setContractManager] = useState<
    PersonReference | undefined
  >();
  const [contractOwner, setContractOwner] = useState<
    PersonReference | undefined
  >();
  const [extensionOptions, setExtensionOptions] = useState<
    ExtensionOptions | undefined
  >();
  const [links, setLinks] = useState<ExternalLink[]>([]);
  const [notes, setNotes] = useState("");
  // Compliance target is stored as an integer 1–100; empty string means
  // "not set" and falls through to the 95 default on read. Zod rejects
  // 0 so we don't let the number input collapse to 0 via clearing.
  const [complianceTarget, setComplianceTarget] = useState<string>("");
  // Per-contract performance gauge thresholds. Empty string = "not
  // set" → submitted as null → backend falls back to the platform
  // defaults (success=90, watch=70). Validation lives in two places:
  // the input min/max attributes (1–100) and a form-level check that
  // watchThreshold < successThreshold when both are provided.
  const [perfSuccessThreshold, setPerfSuccessThreshold] =
    useState<string>("");
  const [perfWatchThreshold, setPerfWatchThreshold] = useState<string>("");
  const [perfThresholdError, setPerfThresholdError] = useState<string | null>(
    null,
  );
  // Per-contract on-time grace window (days) for meetings. Three modes
  // exclusively driven by the radio at the form level:
  //   "default"  → submit { meetingGraceDays: null, byFrequency: null }
  //   "single"   → submit { meetingGraceDays: <num>, byFrequency: null }
  //   "by-freq"  → submit { meetingGraceDays: null, byFrequency: { … } }
  // Hydration on edit picks the mode from whichever field is populated;
  // the per-frequency map wins when both happen to be set on legacy data.
  const [graceMode, setGraceMode] = useState<GraceMode>("default");
  const [meetingGraceDays, setMeetingGraceDays] = useState<string>("");
  const [graceByFreq, setGraceByFreq] = useState<GraceByFrequency>({
    daily: "",
    weekly: "",
    monthly: "",
    quarterly: "",
    annually: "",
    oneOff: "",
  });
  // gstApplies is a tri-state on read (true / false / unset) but a
  // checkbox is binary. We default unchecked on Create and render
  // "not yet recorded" on display surfaces while the contract has
  // never had the box ticked. After the user toggles the box once
  // (in either direction) the value flips between true and false on
  // every subsequent submission — there's no UI affordance to return
  // to "unset" because that's a legacy state, not a user intent.
  const [gstApplies, setGstApplies] = useState<boolean>(false);
  const [gstAppliesTouched, setGstAppliesTouched] = useState<boolean>(false);
  // Per-contract number format for SOWs and variations. The default
  // `"nn"` matches the platform default — plain zero-padded sequentials
  // (`01`, `02`, ...). The alternative is year-prefixed
  // (`SOW-2026-001`, `VAR-2026-001`). Switching mid-contract starts a
  // fresh counter; items already allocated under the previous shape
  // remain reserved but are ignored by the next-number sentinel.
  const [sowNumberFormat, setSowNumberFormat] = useState<
    "nn" | "sow-yyyy-nnn"
  >("nn");
  const [variationNumberFormat, setVariationNumberFormat] = useState<
    "nn" | "var-yyyy-nnn"
  >("nn");
  const [error, setError] = useState<string | null>(null);

  // Pattern 0: seed form fields during render when the existing contract
  // loads from the query. Guard on the contract id so fields are not
  // re-seeded on every render while the user is editing. Narrowing on
  // `existingContract` directly (rather than a derived id-or-null) lets
  // contractToFormState take a real ManagedContract with no `!` assertions.
  const [seededForContractId, setSeededForContractId] = useState<string | null>(null);
  if (existingContract && existingContract.id !== seededForContractId) {
    setSeededForContractId(existingContract.id);
    const seeded = contractToFormState(existingContract);
    setType(seeded.type);
    setContractNumber(seeded.contractNumber);
    setTitle(seeded.title);
    setDescription(seeded.description);
    setSupplierId(seeded.supplierId);
    setSupplierName(seeded.supplierName);
    setContractTypeId(seeded.contractTypeId);
    setBusinessUnitId(seeded.businessUnitId);
    setProcurementMethodId(seeded.procurementMethodId);
    setStartDate(seeded.startDate);
    setEndDate(seeded.endDate);
    setValue(seeded.value);
    setContractManager(seeded.contractManager);
    setContractOwner(seeded.contractOwner);
    setExtensionOptions(seeded.extensionOptions);
    setLinks(seeded.links);
    setNotes(seeded.notes);
    setComplianceTarget(seeded.complianceTarget);
    setPerfSuccessThreshold(seeded.perfSuccessThreshold);
    setPerfWatchThreshold(seeded.perfWatchThreshold);
    setGraceMode(seeded.graceMode);
    setMeetingGraceDays(seeded.meetingGraceDays);
    setGraceByFreq(seeded.graceByFreq);
    // Hydrate the GST checkbox from stored intent if present; mark it as
    // touched so a subsequent submit preserves the explicit boolean (true
    // OR false). When the contract has never had the field set, leave the
    // box unchecked + untouched so display surfaces continue to render the
    // placeholder.
    if (seeded.gstAppliesTouched) {
      setGstApplies(seeded.gstApplies);
      setGstAppliesTouched(true);
    }
    setSowNumberFormat(seeded.sowNumberFormat);
    setVariationNumberFormat(seeded.variationNumberFormat);
  }

  const contractTypeLabel =
    contractTypes?.items?.find((i) => i.itemId === contractTypeId)?.label || "";
  const businessUnitLabel =
    businessUnits?.items?.find((i) => i.itemId === businessUnitId)?.label || "";
  const procurementMethodLabel =
    procurementMethods?.items?.find((i) => i.itemId === procurementMethodId)
      ?.label || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (
      !title ||
      !contractNumber ||
      !supplierId ||
      !contractTypeId ||
      !startDate ||
      !endDate
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    setPerfThresholdError(null);
    if (
      perfSuccessThreshold.trim() !== "" &&
      perfWatchThreshold.trim() !== "" &&
      Number(perfWatchThreshold) >= Number(perfSuccessThreshold)
    ) {
      setPerfThresholdError(
        "Watch threshold must be less than the success threshold.",
      );
      return;
    }

    const baseFields = {
      type,
      contractNumber,
      title,
      description: description || undefined,
      supplierId,
      supplierName,
      contractTypeId,
      contractTypeLabel,
      businessUnitId: businessUnitId || undefined,
      businessUnitLabel: businessUnitLabel || undefined,
      procurementMethodId: procurementMethodId || undefined,
      procurementMethodLabel: procurementMethodLabel || undefined,
      startDate,
      endDate,
      value,
      // contractPeople is the canonical roster. Build it from the two
      // PersonReference inputs; entries without a name are omitted so the
      // backend Zod schema's exactly-one-owner / exactly-one-manager
      // invariant surfaces a meaningful validation error instead of an
      // empty-name shape error. alertsEnabled defaults to true matching
      // the mapper synthesis; manager.alertsEnabled is server-enforced
      // anyway.
      contractPeople: buildContractPeople(contractOwner, contractManager),
      extensionOptions,
      links: links.length > 0 ? links : undefined,
      notes: notes || undefined,
      complianceTarget:
        complianceTarget.trim() === "" ? null : Number(complianceTarget),
      performanceSuccessThreshold:
        perfSuccessThreshold.trim() === ""
          ? null
          : Number(perfSuccessThreshold),
      performanceWatchThreshold:
        perfWatchThreshold.trim() === "" ? null : Number(perfWatchThreshold),
      meetingGraceDays:
        graceMode === "single" && meetingGraceDays.trim() !== ""
          ? Number(meetingGraceDays)
          : null,
      meetingGraceDaysByFrequency:
        graceMode === "by-freq"
          ? (() => {
              const map: Record<string, number> = {};
              for (const [k, v] of Object.entries(graceByFreq)) {
                if (v.trim() !== "") map[k] = Number(v);
              }
              return Object.keys(map).length > 0 ? map : null;
            })()
          : null,
      // Only emit gstApplies when the user has interacted with the
      // checkbox at least once — otherwise the field stays absent
      // from the payload and the contract retains its "GST status not
      // recorded" placeholder on display surfaces.
      gstApplies: gstAppliesTouched ? gstApplies : undefined,
      sowNumberFormat,
      variationNumberFormat,
    };

    try {
      if (isEditing && existingContract) {
        const input: UpdateManagedContractInput = {
          ...baseFields,
          status: existingContract.status,
          version: existingContract.version,
        };
        await updateMutation.mutateAsync({ id, input });
        navigate(`/manage/contracts/${id}`);
      } else {
        const input: CreateManagedContractInput = {
          ...baseFields,
          status: "draft",
        };
        const created = await createMutation.mutateAsync(input);
        navigate(`/manage/contracts/${created.id}`);
      }
    } catch (err) {
      // Translate raw Zod-style error strings ("contractPeople.0.email: …")
      // into friendly user copy. The role labels in the formatter are
      // sourced from the people array we just built so error indices map
      // to the right person.
      const friendly = formatBackendError(err, {
        contractPeople: buildContractPeople(contractOwner, contractManager),
      });
      setError(
        friendly ||
          `Failed to ${isEditing ? "update" : "create"} contract.`,
      );
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEditing && isLoadingContract) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link
        to="/manage/contracts"
        className="inline-flex items-center gap-1 text-caption text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400 focus:outline-hidden focus:ring-2 focus:ring-brand-400 rounded-sm"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        Back to register
      </Link>
      <div>
        <h1 className="text-h1 text-slate-900 dark:text-slate-50">
          {isEditing ? "Edit Contract" : "New Contract"}
        </h1>
        <p className="mt-1 text-body text-slate-600 dark:text-slate-400">
          {isEditing
            ? "Update the contract's identity, parties, dates, value and grading rules."
            : "Register a contract for management. The fields below capture identity, parties, dates, value and grading rules."}
          <span className="ml-1 text-caption text-slate-500 dark:text-slate-400">
            Required fields marked *
          </span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <Alert variant="error">{error}</Alert>}

        <div className="card p-6 space-y-4">
          <h2 className="text-h3 text-slate-900 dark:text-slate-50">
            Contract identity
          </h2>
          <div>
            <label htmlFor="type" className="label">
              Type *
            </label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as ManagedContractType)}
              className="input"
              disabled={isPending}
            >
              <option value="contract">Contract</option>
              <option value="arrangement">Arrangement</option>
            </select>
          </div>

          <div>
            <label htmlFor="contractNumber" className="label">
              Contract Number *
            </label>
            <input
              id="contractNumber"
              type="text"
              value={contractNumber}
              onChange={(e) => setContractNumber(e.target.value)}
              className="input"
              required
              disabled={isPending}
            />
          </div>

          <div>
            <label htmlFor="title" className="label">
              Title *
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input"
              required
              disabled={isPending}
            />
          </div>

          <div>
            <label htmlFor="description" className="label">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input min-h-[80px]"
              disabled={isPending}
            />
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <h2 className="text-h3 text-slate-900 dark:text-slate-50">
            Supplier &amp; Classification
          </h2>

          <div>
            <label className="label">Supplier *</label>
            <SupplierCombobox
              value={supplierId}
              onChange={(id, name) => {
                setSupplierId(id);
                setSupplierName(name);
              }}
              onCreateNew={() => setShowSupplierModal(true)}
            />
          </div>

          <div>
            <label htmlFor="contractType" className="label">
              Contract Category *
            </label>
            <select
              id="contractType"
              value={contractTypeId}
              onChange={(e) => setContractTypeId(e.target.value)}
              className="input"
              required
              disabled={isPending}
            >
              <option value="">Select...</option>
              {contractTypes?.items
                ?.filter((i) => i.isActive)
                .map((i) => (
                  <option key={i.itemId} value={i.itemId}>
                    {i.label}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label htmlFor="businessUnit" className="label">
              Business Unit
            </label>
            <select
              id="businessUnit"
              value={businessUnitId}
              onChange={(e) => setBusinessUnitId(e.target.value)}
              className="input"
              disabled={isPending}
            >
              <option value="">Select... (optional)</option>
              {businessUnits?.items
                ?.filter((i) => i.isActive)
                .map((i) => (
                  <option key={i.itemId} value={i.itemId}>
                    {i.label}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label htmlFor="procurementMethod" className="label">
              Procurement Method
            </label>
            <select
              id="procurementMethod"
              value={procurementMethodId}
              onChange={(e) => setProcurementMethodId(e.target.value)}
              className="input"
              disabled={isPending}
            >
              <option value="">Select... (optional)</option>
              {procurementMethods?.items
                ?.filter((i) => i.isActive)
                .map((i) => (
                  <option key={i.itemId} value={i.itemId}>
                    {i.label}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <h2 className="text-h3 text-slate-900 dark:text-slate-50">
            Dates &amp; Value
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="startDate" className="label">
                Start Date *
              </label>
              <input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input"
                required
                disabled={isPending}
              />
            </div>
            <div>
              <label htmlFor="endDate" className="label">
                End Date *
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input"
                required
                disabled={isPending}
              />
            </div>
          </div>

          <div>
            <label htmlFor="value" className="label">
              Value
            </label>
            <CurrencyInput
              id="value"
              value={value}
              onChange={setValue}
              disabled={isPending}
              allowDecimal
            />
            <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
              Enter the contract value <strong>excluding GST</strong>. When the
              GST checkbox below is ticked, the inc-GST figure is derived from
              this value (× 1.1).
            </p>
          </div>

          <div>
            <label
              htmlFor="gstApplies"
              className="inline-flex items-center gap-2 text-body text-slate-700 dark:text-slate-300"
            >
              <input
                id="gstApplies"
                type="checkbox"
                className="accent-brand-600"
                checked={gstApplies}
                onChange={(e) => {
                  setGstApplies(e.target.checked);
                  setGstAppliesTouched(true);
                }}
                disabled={isPending}
              />
              GST applies to this contract
            </label>
            <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
              When ticked, dollar values on the contract details page show both
              ex-GST and inc-GST. Leave unticked for GST-free contracts. Until
              this is set, milestones surfaces note "GST status not recorded".
            </p>
          </div>

          <ExtensionOptionsInput
            value={extensionOptions}
            onChange={setExtensionOptions}
            disabled={isPending}
          />
        </div>

        <div className="card p-6 space-y-4">
          <h2 className="text-h3 text-slate-900 dark:text-slate-50">
            People &amp; Notes
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PersonReferenceInput
              label="Contract Manager"
              value={contractManager}
              onChange={setContractManager}
              disabled={isPending}
              alertsLocked
              nameRequired
              availableUsers={availableUsers}
            />
            <PersonReferenceInput
              label="Contract Owner"
              value={contractOwner}
              onChange={setContractOwner}
              nameRequired
              disabled={isPending}
              availableUsers={availableUsers}
            />
          </div>

          <div>
            <label htmlFor="notes" className="label">
              Notes
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input min-h-[80px]"
              disabled={isPending}
              maxLength={2000}
            />
          </div>

          <ExternalLinksList
            value={links}
            onChange={setLinks}
            disabled={isPending}
          />
        </div>

        <ContractSettingsCard
          complianceTarget={complianceTarget}
          onComplianceTargetChange={setComplianceTarget}
          perfSuccessThreshold={perfSuccessThreshold}
          onPerfSuccessThresholdChange={setPerfSuccessThreshold}
          perfWatchThreshold={perfWatchThreshold}
          onPerfWatchThresholdChange={setPerfWatchThreshold}
          perfThresholdError={perfThresholdError}
          graceMode={graceMode}
          onGraceModeChange={setGraceMode}
          meetingGraceDays={meetingGraceDays}
          onMeetingGraceDaysChange={setMeetingGraceDays}
          graceByFreq={graceByFreq}
          onGraceByFreqChange={(key, value) =>
            setGraceByFreq((prev) => ({ ...prev, [key]: value }))
          }
          disabled={isPending}
        />

        <div className="card p-6 space-y-4">
          <h2 className="text-h3 text-slate-900 dark:text-slate-50">
            Numbering
          </h2>
          <p className="text-caption text-slate-500 dark:text-slate-400">
            How SOWs and variations on this contract are numbered. Switching
            format starts a new counter from 1; previously allocated numbers
            stay as they are.
          </p>

          <div>
            <label htmlFor="sowNumberFormat" className="label">
              SOW number format
            </label>
            <select
              id="sowNumberFormat"
              value={sowNumberFormat}
              onChange={(e) =>
                setSowNumberFormat(
                  e.target.value as "nn" | "sow-yyyy-nnn",
                )
              }
              className="input max-w-md"
              disabled={isPending}
            >
              <option value="nn">Sequential (01, 02, 03)</option>
              <option value="sow-yyyy-nnn">
                Year-prefixed (SOW-2026-001)
              </option>
            </select>
          </div>

          <div>
            <label htmlFor="variationNumberFormat" className="label">
              Variation number format
            </label>
            <select
              id="variationNumberFormat"
              value={variationNumberFormat}
              onChange={(e) =>
                setVariationNumberFormat(
                  e.target.value as "nn" | "var-yyyy-nnn",
                )
              }
              className="input max-w-md"
              disabled={isPending}
            >
              <option value="nn">Sequential (01, 02, 03)</option>
              <option value="var-yyyy-nnn">
                Year-prefixed (VAR-2026-001)
              </option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate("/manage/contracts")}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            {isEditing ? "Save Changes" : "Create Contract"}
          </Button>
        </div>
      </form>

      <SupplierFormModal
        isOpen={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        onSubmit={async (data) => {
          const created = await createSupplierMutation.mutateAsync(data);
          setSupplierId(created.id);
          setSupplierName(created.name);
          setShowSupplierModal(false);
        }}
        isLoading={createSupplierMutation.isPending}
      />
    </div>
  );
}
